import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Unified new-account bootstrap. Runs once per session for any sign-in path
 * (email/password, Google, future providers):
 *  1. makes sure a Xamseen profile row exists
 *  2. reports the onboarding state so the client can route correctly
 *  3. sends the welcome email exactly once, guarded by profiles.welcome_email_sent_at
 * Email failures are logged and never block access.
 */
export const bootstrapAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: existing } = await supabase
      .from("profiles")
      .select("id, username, first_name, full_name, onboarded, onboarding_step, welcome_email_sent_at, welcome_email_attempts")
      .eq("id", userId)
      .maybeSingle();

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let profile = existing;

    if (!profile) {
      // Safety net: OAuth accounts created before the trigger existed, or a
      // trigger failure. Never leave a signed-in user without a profile.
      const { data: authUser } = await supabaseAdmin.auth.admin.getUserById(userId);
      const meta = (authUser?.user?.user_metadata ?? {}) as Record<string, string | undefined>;
      const email = authUser?.user?.email ?? "";
      const fullName = meta["full_name"] ?? meta["name"] ?? "";
      const base = (email.split("@")[0] ?? "member").toLowerCase().replace(/[^a-z0-9_]/g, "") || "member";
      const username = `${base.slice(0, 14)}_${userId.slice(0, 6)}`;

      const { data: created } = await supabaseAdmin
        .from("profiles")
        .insert({
          id: userId,
          username,
          full_name: fullName,
          first_name: meta["given_name"] ?? meta["first_name"] ?? fullName.split(" ")[0] ?? null,
          last_name: meta["family_name"] ?? meta["last_name"] ?? null,
          avatar_url: meta["avatar_url"] ?? meta["picture"] ?? null,
        })
        .select("id, username, first_name, full_name, onboarded, onboarding_step, welcome_email_sent_at, welcome_email_attempts")
        .maybeSingle();
      profile = created ?? null;
    }

    const { ensureWelcomeEmail } = await import("@/lib/email/welcome.server");
    const welcomeEmail = profile ? await ensureWelcomeEmail(userId) : "no_profile";




    return {
      profileReady: Boolean(profile),
      username: profile?.username ?? null,
      firstName: profile?.first_name ?? (profile?.full_name ? profile.full_name.split(" ")[0] : null),
      onboarded: Boolean(profile?.onboarded),
      onboardingStep: profile?.onboarding_step ?? 0,
      welcomeEmail,
    };
  });

/** Admin-only onboarding + email health metrics. */
export const getOnboardingHealth = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const since = new Date(Date.now() - 7 * 864e5).toISOString();

    const [total, newUsers, onboarded, welcomeSent, emailFailed, emailSent] = await Promise.all([
      supabaseAdmin.from("profiles").select("id", { count: "exact", head: true }),
      supabaseAdmin.from("profiles").select("id", { count: "exact", head: true }).gte("created_at", since),
      supabaseAdmin.from("profiles").select("id", { count: "exact", head: true }).eq("onboarded", true),
      supabaseAdmin.from("profiles").select("id", { count: "exact", head: true }).not("welcome_email_sent_at", "is", null),
      supabaseAdmin.from("email_events").select("id", { count: "exact", head: true }).eq("status", "failed"),
      supabaseAdmin.from("email_events").select("id", { count: "exact", head: true }).eq("status", "sent"),
    ]);
    const users = total.count ?? 0;
    const onboardedCount = onboarded.count ?? 0;

    return {
      users,
      newUsers7d: newUsers.count ?? 0,
      onboardingCompleted: onboardedCount,
      onboardingIncomplete: Math.max(0, users - onboardedCount),
      welcomeEmailsSent: welcomeSent.count ?? 0,
      emailsSent: emailSent.count ?? 0,
      emailsFailed: emailFailed.count ?? 0,
    };
  });

/** Saves onboarding progress step by step so a half-finished flow is never lost. */
export const saveOnboardingProgress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: {
    step: number;
    country?: string | null;
    region?: string | null;
    city?: string | null;
    neighborhood?: string | null;
    interests?: string[];
    full_name?: string | null;
    username?: string | null;
    bio?: string | null;
    avatar_url?: string | null;
    complete?: boolean;
  }) => input)
  .handler(async ({ data, context }) => {
    const patch: {
      onboarding_step: number;
      country?: string | null; region?: string | null; city?: string | null; neighborhood?: string | null;
      full_name?: string | null; username?: string; bio?: string | null;
      avatar_url?: string | null; interests?: string[]; location?: string; onboarded?: boolean;
    } = { onboarding_step: data.step };
    for (const key of ["country", "region", "city", "neighborhood", "full_name", "bio", "avatar_url"] as const) {
      if (data[key] !== undefined) patch[key] = data[key];
    }
    if (data.username) patch.username = data.username;
    if (data.interests) patch.interests = data.interests;
    if (data.city || data.region || data.country) {
      patch.location = [data.city, data.region, data.country].filter(Boolean).join(", ");
    }
    if (data.complete) patch.onboarded = true;

    const { error } = await context.supabase.from("profiles").update(patch).eq("id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin-only: retry a welcome email that failed at the provider. */
export const retryWelcomeEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { userId: string }) => input)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { resendWelcomeEmail } = await import("@/lib/email/welcome.server");
    return { state: await resendWelcomeEmail(data.userId) };
  });
